import {
  $createCodeNode,
  $isCodeNode,
  getCodeLanguages,
  getDefaultCodeLanguage,
} from "@lexical/code";
import { $isLinkNode, TOGGLE_LINK_COMMAND } from "@lexical/link";
import {
  $isListNode,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  ListNode,
  REMOVE_LIST_COMMAND,
} from "@lexical/list";
import {
  Course_created_successfully_image,
  google_drive,
  uplode_file,
  magicpen,
} from "../../../../../assets/images/images";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $createHeadingNode,
  $createQuoteNode,
  $isHeadingNode,
} from "@lexical/rich-text";
import {
  $isAtNodeEnd,
  $isParentElementRTL,
  $wrapNodes,
} from "@lexical/selection";
import { $getNearestNodeOfType, mergeRegister } from "@lexical/utils";
import {
  $createParagraphNode,
  $getNodeByKey,
  $getSelection,
  $isRangeSelection,
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  FORMAT_ELEMENT_COMMAND,
  FORMAT_TEXT_COMMAND,
  REDO_COMMAND,
  SELECTION_CHANGE_COMMAND,
  UNDO_COMMAND,
  $insertNodes,
  $getRoot,
  $createTextNode,
} from "lexical";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DOMPurify from "dompurify";
import katex from "katex";
import { $generateHtmlFromNodes, $generateNodesFromDOM } from "@lexical/html";
import { createPortal } from "react-dom";
import FilteredInput from "../../../../../components/FilteredInput/FilteredInput";
import InsertMathButton from "../../dashboard/teacher/lessons/InsertMathButton";
import { useSelector } from "react-redux";
import {
  addReFormatter,
  resetReFormatter,
} from "../../../../../state/actions/reFormatterAction";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { marked } from "marked";
import MarkdownIt from "markdown-it";
// Security: removed unused `markdown-it-katex` import — it had a known XSS vulnerability
// (GHSA-5ff8-jcf9-fw62) with no upstream fix. The package was imported but never
// invoked; if/when KaTeX rendering is needed here, integrate `katex` directly with
// DOMPurify sanitization (see KatexComponent.js for the pattern).
import "katex/dist/katex.min.css";
import { Tooltip } from "react-tooltip";
import MathKeyboardModal from "../../../../../components/MathEditor/MathKeyboardModal";
import { IconButton } from "@mui/material";
import KeyboardIcon from "@mui/icons-material/Keyboard";
import { MathNode } from "../../../../../../src/pages/home/components/dashboard/teacher/lessons/MathNode";
import { addLineBreaksAroundImages } from "../../dashboard/teacher/lessons/NewLessons/utils/fileHelpers";
const LowPriority = 1;

const supportedBlockTypes = new Set([
  "paragraph",
  "quote",
  "code",
  "h1",
  "h2",
  "ul",
  "ol",
]);

const blockTypeToBlockName = {
  code: "Code Block",
  h1: "Large Heading",
  h2: "Small Heading",
  h3: "Heading",
  h4: "Heading",
  h5: "Heading",
  ol: "Numbered List",
  paragraph: "Normal",
  quote: "Quote",
  ul: "Bulleted List",
};

function Divider() {
  return <div className="divider" />;
}

function positionEditorElement(editor, rect) {
  if (rect === null) {
    editor.style.opacity = "0";
    editor.style.top = "-1000px";
    editor.style.left = "-1000px";
  } else {
    editor.style.opacity = "1";
    editor.style.top = `${rect.top + rect.height + window.pageYOffset + 10}px`;
    editor.style.left = `${
      rect.left + window.pageXOffset - editor.offsetWidth / 2 + rect.width / 2
    }px`;
  }
}

function FloatingLinkEditor({ editor }) {
  const editorRef = useRef(null);
  const inputRef = useRef(null);
  const mouseDownRef = useRef(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [isEditMode, setEditMode] = useState(false);
  const [lastSelection, setLastSelection] = useState(null);

  const updateLinkEditor = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      const node = getSelectedNode(selection);
      const parent = node.getParent();
      if ($isLinkNode(parent)) {
        setLinkUrl(parent.getURL());
      } else if ($isLinkNode(node)) {
        setLinkUrl(node.getURL());
      } else {
        setLinkUrl("");
      }
    }
    const editorElem = editorRef.current;
    const nativeSelection = window.getSelection();
    const activeElement = document.activeElement;

    if (editorElem === null) {
      return;
    }

    const rootElement = editor.getRootElement();
    if (
      selection !== null &&
      !nativeSelection.isCollapsed &&
      rootElement !== null &&
      rootElement.contains(nativeSelection.anchorNode)
    ) {
      const domRange = nativeSelection.getRangeAt(0);
      let rect;
      if (nativeSelection.anchorNode === rootElement) {
        let inner = rootElement;
        while (inner.firstElementChild != null) {
          inner = inner.firstElementChild;
        }
        rect = inner.getBoundingClientRect();
      } else {
        rect = domRange.getBoundingClientRect();
      }

      if (!mouseDownRef.current) {
        positionEditorElement(editorElem, rect);
      }
      setLastSelection(selection);
    } else if (!activeElement || activeElement.className !== "link-input") {
      positionEditorElement(editorElem, null);
      setLastSelection(null);
      setEditMode(false);
      setLinkUrl("");
    }

    return true;
  }, [editor]);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateLinkEditor();
        });
      }),

      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          updateLinkEditor();
          return true;
        },
        LowPriority,
      ),
    );
  }, [editor, updateLinkEditor]);

  useEffect(() => {
    editor.getEditorState().read(() => {
      updateLinkEditor();
    });
  }, [editor, updateLinkEditor]);

  useEffect(() => {
    if (isEditMode && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditMode]);

  return (
    <div ref={editorRef} className="link-editor">
      {isEditMode ? (
        <input
          ref={inputRef}
          className="link-input"
          value={linkUrl}
          onChange={(event) => {
            setLinkUrl(event.target.value);
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              if (lastSelection !== null) {
                if (linkUrl !== "") {
                  editor.dispatchCommand(TOGGLE_LINK_COMMAND, linkUrl);
                }
                setEditMode(false);
              }
            } else if (event.key === "Escape") {
              event.preventDefault();
              setEditMode(false);
            }
          }}
        />
      ) : (
        <>
          <div className="link-input">
            <a href={linkUrl} target="_blank" rel="noopener noreferrer">
              {linkUrl}
            </a>
            <div
              className="link-edit"
              role="button"
              tabIndex={0}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                setEditMode(true);
              }}
            />
          </div>
        </>
      )}
    </div>
  );
}

function Select({ onChange, className, options, value }) {
  return (
    <select className={className} onChange={onChange} value={value}>
      <option hidden={true} value="" />
      {options.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </select>
  );
}

function getSelectedNode(selection) {
  const anchor = selection.anchor;
  const focus = selection.focus;
  const anchorNode = selection.anchor.getNode();
  const focusNode = selection.focus.getNode();
  if (anchorNode === focusNode) {
    return anchorNode;
  }
  const isBackward = selection.isBackward();
  if (isBackward) {
    return $isAtNodeEnd(focus) ? anchorNode : focusNode;
  } else {
    return $isAtNodeEnd(anchor) ? focusNode : anchorNode;
  }
}

function BlockOptionsDropdownList({
  editor,
  blockType,
  toolbarRef,
  setShowBlockOptionsDropDown,
}) {
  const dropDownRef = useRef(null);

  useEffect(() => {
    const toolbar = toolbarRef.current;
    const dropDown = dropDownRef.current;

    if (toolbar !== null && dropDown !== null) {
      const { top, left } = toolbar.getBoundingClientRect();
      dropDown.style.top = `${top + 40}px`;
      dropDown.style.left = `${left}px`;
    }
  }, [dropDownRef, toolbarRef]);

  useEffect(() => {
    const dropDown = dropDownRef.current;
    const toolbar = toolbarRef.current;

    if (dropDown !== null && toolbar !== null) {
      const handle = (event) => {
        const target = event.target;

        if (!dropDown.contains(target) && !toolbar.contains(target)) {
          setShowBlockOptionsDropDown(false);
        }
      };
      document.addEventListener("click", handle);

      return () => {
        document.removeEventListener("click", handle);
      };
    }
  }, [dropDownRef, setShowBlockOptionsDropDown, toolbarRef]);

  const formatParagraph = () => {
    if (blockType !== "paragraph") {
      editor.update(() => {
        const selection = $getSelection();

        if ($isRangeSelection(selection)) {
          $wrapNodes(selection, () => $createParagraphNode());
        }
      });
    }
    setShowBlockOptionsDropDown(false);
  };

  const formatLargeHeading = () => {
    if (blockType !== "h1") {
      editor.update(() => {
        const selection = $getSelection();

        if ($isRangeSelection(selection)) {
          $wrapNodes(selection, () => $createHeadingNode("h1"));
        }
      });
    }
    setShowBlockOptionsDropDown(false);
  };

  const formatSmallHeading = () => {
    if (blockType !== "h2") {
      editor.update(() => {
        const selection = $getSelection();

        if ($isRangeSelection(selection)) {
          $wrapNodes(selection, () => $createHeadingNode("h2"));
        }
      });
    }
    setShowBlockOptionsDropDown(false);
  };

  const formatBulletList = () => {
    if (blockType !== "ul") {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND);
    } else {
      editor.dispatchCommand(REMOVE_LIST_COMMAND);
    }
    setShowBlockOptionsDropDown(false);
  };

  const formatNumberedList = () => {
    if (blockType !== "ol") {
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND);
    } else {
      editor.dispatchCommand(REMOVE_LIST_COMMAND);
    }
    setShowBlockOptionsDropDown(false);
  };

  const formatQuote = () => {
    if (blockType !== "quote") {
      editor.update(() => {
        const selection = $getSelection();

        if ($isRangeSelection(selection)) {
          $wrapNodes(selection, () => $createQuoteNode());
        }
      });
    }
    setShowBlockOptionsDropDown(false);
  };

  const formatCode = () => {
    if (blockType !== "code") {
      editor.update(() => {
        const selection = $getSelection();

        if ($isRangeSelection(selection)) {
          $wrapNodes(selection, () => $createCodeNode());
        }
      });
    }
    setShowBlockOptionsDropDown(false);
  };

  return (
    <div className="dropdown" ref={dropDownRef}>
      <button className="item" onClick={formatParagraph}>
        <span className="icon paragraph" />
        <span className="text">Normal</span>
        {blockType === "paragraph" && <span className="active" />}
      </button>
      <button className="item" onClick={formatLargeHeading}>
        <span className="icon large-heading" />
        <span className="text">Large Heading</span>
        {blockType === "h1" && <span className="active" />}
      </button>
      <button className="item" onClick={formatSmallHeading}>
        <span className="icon small-heading" />
        <span className="text">Small Heading</span>
        {blockType === "h2" && <span className="active" />}
      </button>
      <button className="item" onClick={formatBulletList}>
        <span className="icon bullet-list" />
        <span className="text">Bullet List</span>
        {blockType === "ul" && <span className="active" />}
      </button>
      <button className="item" onClick={formatNumberedList}>
        <span className="icon numbered-list" />
        <span className="text">Numbered List</span>
        {blockType === "ol" && <span className="active" />}
      </button>
      <button className="item" onClick={formatQuote}>
        <span className="icon quote" />
        <span className="text">Quote</span>
        {blockType === "quote" && <span className="active" />}
      </button>
      <button className="item" onClick={formatCode}>
        <span className="icon code" />
        <span className="text">Code Block</span>
        {blockType === "code" && <span className="active" />}
      </button>
    </div>
  );
}

export default function ToolbarPlugin({
  showInputTooltip,
  setShowInputTooltip,
  latextextvalue,
  setLatextextvalue,
  oldMathNode,
  oldnodeeditor,
  handleEnhanceText,
  reFormatterLoading,
  reFormatterData,
  setReFormatterData,
  ganrateLesson,
  isLeftPanelOpen,
  disableEnhanceText = false,
}) {
  const [editor] = useLexicalComposerContext();
  const toolbarRef = useRef(null);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [blockType, setBlockType] = useState("paragraph");
  const [selectedElementKey, setSelectedElementKey] = useState(null);
  const [showBlockOptionsDropDown, setShowBlockOptionsDropDown] =
    useState(false);
  const [codeLanguage, setCodeLanguage] = useState("");
  const [isRTL, setIsRTL] = useState(false);
  const [isLink, setIsLink] = useState(false);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const [isCode, setIsCode] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);

  const openKeyboard = () => {
    setIsModalOpen(true);
    setLatextextvalue(null);
  };

  const closeKeyboard = () => {
    setIsModalOpen(false);
    setShowInputTooltip(false);
    setLatextextvalue(null);
  };
  // const [showInputTooltip, setShowInputTooltip] = useState(false); // New state to control tooltip visibility
  const [inputValue, setInputValue] = useState(
    latextextvalue ? latextextvalue : "",
  ); // State to hold input value
  useEffect(() => {
    if (latextextvalue) {
      setInputValue();
    }
  }, [latextextvalue]);

  useEffect(() => {
    if (reFormatterData) {
      const addBackSlashes = (input) => {
        // Regular expression to match LaTeX math wrapped in $$ symbols
        const latexMathRegex = /\$\$(.*?)\$\$/gs;

        // Replace LaTeX math content inside $$ symbols
        return input.replace(latexMathRegex, (match, latexContent) => {
          // Step 1: Check if there are curly braces inside the latexContent
          // and if there's already a backslash before them, add one more backslash
          let modifiedContent = latexContent.replace(
            /(\\?)(\{|\})/g,
            (fullMatch, backslash, curlyBrace) => {
              // If there is already a backslash before the curly brace, add one more backslash

              if (backslash === "\\") {
                return "\\\\" + curlyBrace; // Add one more backslash before the curly brace
              }
              // If no backslash, just return the curly brace as is
              return curlyBrace;
            },
          );

          // Step 2: Return the modified LaTeX math content wrapped in $$ symbols
          return `$$${modifiedContent}$$`;
        });
      };

      const processLatex = (input) => {
        // Regular expression to match any LaTeX matrix type (pmatrix, bmatrix, vmatrix, Vmatrix, matrix)
        const matrixRegex =
          /\\begin{(pmatrix|bmatrix|vmatrix|Vmatrix|matrix)}(.*?)\\end{\1}/gs;

        // Replace LaTeX content inside any matrix and double every backslash
        return input.replace(
          matrixRegex,
          (match, matrixType, matrixContent) => {
            // Replace every single backslash with a double backslash inside the matrix content
            const modifiedMatrixContent = matrixContent.replace(/\\/g, "\\\\");
            // Return the LaTeX with the same matrix type (pmatrix, bmatrix, etc.) wrapped around the modified content
            return `\\begin{${matrixType}}${modifiedMatrixContent}\\end{${matrixType}}`;
          },
        );
      };
      const addNewLine = addLineBreaksAroundImages(marked(reFormatterData));

      // const latex = String.raw`$$A = \\{1, 2, 3, 4, 5\\} $$`;
      const addBackSlash = addBackSlashes(addNewLine);
      // const addBackSlash = addBackSlashes('$$ AB = \\begin{pmatrix} (2 \times 1 + 3 \times 0) & (2 \times 2 + 3 \times 3) \\ (4 \times 1 + 5 \times 0) & (4 \times 2 + 5 \times 3) \end{pmatrix}\begin{pmatrix} 2 & 13 \\ 4 & 23 \\end{pmatrix} $$');
      // const addBackSlash = addBackSlashes('$$E = m c^2$$\n\n$$a^2 + b^2 = c^2$$\n\n$$\\int_{a}^{b} f(x) \\, dx = F(b) - F(a)$$\n\n$$\\sum_{i=1}^{n} i = \\frac{n(n+1)}{2}$$\n\n$$A = \\pi r^2$$\n\n$$V = \\frac{4}{3} \\pi r^3$$\n\n$$F = G \\frac{m_1 m_2}{r^2}$$\n\n$$s = ut + \\frac{1}{2}a t^2$$\n\n$$PV = nRT$$\n\n$$\\nabla \\cdot E = \\frac{\\rho}{\\varepsilon_0}$$\n\n$$\\nabla \\times E = -\\frac{\\partial B}{\\partial t}$$\n\n$$\\oint_{\\partial S} F \\cdot dR = \\iint_{S} (\\nabla \\times F) \\cdot dS$$\n\n$$i\\hbar \\frac{\\partial}{\\partial t} \\Psi = \\hat{H} \\Psi$$\n\n$$\\psi(x) = A e^{ikx} + B e^{-ikx}$$\n\n$$\\lambda = \\frac{h}{mv}$$\n\n$$\\sigma = \\sqrt{\\frac{1}{N} \\sum_{i=1}^{N} (x_i - \\mu)^2}$$\n\n$$Z = \\sum_{s} e^{-\\beta E_s}$$\n\n$$\\Delta S = \\int \\frac{dQ}{T}$$');

      const latex = String.raw`${addBackSlash}`;

      // const demo = processLatex(latex);
      const demo = latex;

      // const demo = marked.parse(reFormatterData, { gfm: false, breaks: false, sanitize: false });

      const formattedData = demo;

      editor.update(() => {
        const parser = new DOMParser();
        const dom = parser.parseFromString(formattedData, "text/html");

        let nodes = $generateNodesFromDOM(editor, dom);
        const root = $getRoot();
        root.clear();
        try {
          $insertNodes(nodes);
          const htmlString = $generateHtmlFromNodes(editor, null);
          let root = $getRoot();
          root.clear();
          const dom = parser.parseFromString(
            `${htmlString}<h1> </h1>`,
            "text/html",
          );
          nodes = $generateNodesFromDOM(editor, dom);
          $insertNodes(nodes);
        } catch (error) {
          console.error(error);
          const root = $getRoot();
          const p = $createParagraphNode();
          p.append($createTextNode(formattedData));
          root.append(p);
        }
      });

      setReFormatterData(null);
    }
  }, [reFormatterData]);

  const toggleInputTooltip = () => {
    setShowInputTooltip(!showInputTooltip); // Toggle tooltip visibility
  };

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      const anchorNode = selection.anchor.getNode();

      // Safely get top-level element without throwing
      let element;
      try {
        element =
          anchorNode.getKey() === "root"
            ? anchorNode
            : anchorNode.getTopLevelElementOrThrow();
      } catch (e) {
        // anchorNode is inside a table cell or other non-standard structure
        // Walk up manually to find the nearest block-level parent
        let current = anchorNode;
        while (current && current.getKey() !== "root") {
          const parent = current.getParent();
          if (!parent || parent.getKey() === "root") {
            element = current;
            break;
          }
          current = parent;
        }
        if (!element) return; // still couldn't resolve, bail out safely
      }

      const elementKey = element.getKey();
      const elementDOM = editor.getElementByKey(elementKey);
      if (elementDOM !== null) {
        setSelectedElementKey(elementKey);
        if ($isListNode(element)) {
          const parentList = $getNearestNodeOfType(anchorNode, ListNode);
          const type = parentList ? parentList.getTag() : element.getTag();
          setBlockType(type);
        } else {
          const type = $isHeadingNode(element)
            ? element.getTag()
            : element.getType();
          setBlockType(type);
          if ($isCodeNode(element)) {
            setCodeLanguage(element.getLanguage() || getDefaultCodeLanguage());
          }
        }
      }
      // Update text format
      setIsBold(selection.hasFormat("bold"));
      setIsItalic(selection.hasFormat("italic"));
      setIsUnderline(selection.hasFormat("underline"));
      setIsStrikethrough(selection.hasFormat("strikethrough"));
      setIsCode(selection.hasFormat("code"));
      setIsRTL($isParentElementRTL(selection));

      // Update links
      const node = getSelectedNode(selection);
      const parent = node.getParent();
      if ($isLinkNode(parent) || $isLinkNode(node)) {
        setIsLink(true);
      } else {
        setIsLink(false);
      }
    }
  }, [editor]);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateToolbar();
        });
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        (_payload, newEditor) => {
          updateToolbar();
          return false;
        },
        LowPriority,
      ),
      editor.registerCommand(
        CAN_UNDO_COMMAND,
        (payload) => {
          setCanUndo(payload);
          return false;
        },
        LowPriority,
      ),
      editor.registerCommand(
        CAN_REDO_COMMAND,
        (payload) => {
          setCanRedo(payload);
          return false;
        },
        LowPriority,
      ),
    );
  }, [editor, updateToolbar]);

  const codeLanguges = useMemo(() => getCodeLanguages(), []);
  const onCodeLanguageSelect = useCallback(
    (e) => {
      editor.update(() => {
        if (selectedElementKey !== null) {
          const node = $getNodeByKey(selectedElementKey);
          if ($isCodeNode(node)) {
            node.setLanguage(e.target.value);
          }
        }
      });
    },
    [editor, selectedElementKey],
  );

  const handleInsert = (latex) => {
    if (!latex) return;

    let normalizedLatex = latex
      .replace(/\\"/g, "\\ddot{a}") // Replace Euler constant
      .replace(/\\'/g, "\\acute{a}") // Replace Euler constant
      .replace(/\\\//g, "\\slash")
      .replace(/\\=/g, "\\overline{a}")
      .replace(/\\Colon/g, "\\mathbin{:}")
      .replace(/\\Laplace/g, "\\mathscr{L}")
      .replace(/\\Lbag/g, "\\langle")
      .replace(/\\Longmapsfrom/g, "\\longleftarrow")
      .replace(/\\Longmapsto/g, "\\longrightarrow")
      .replace(/\\MapsTo/g, "\\rightarrow")
      .replace(/\\Mapsfrom/g, "\\leftarrow")
      .replace(/\\Rbag/g, "\\rangle")
      .replace(/\\Yup/g, "\\checkmark")
      .replace(/\\^/g, "\\hat{a}")
      .replace(/\\`/g, "\\grave{a}")
      .replace(/\\backdoubleprime/g, "\\prime\\prime") // or ''
      .replace(/\\biconditional/g, "\\leftrightarrow") // or \iff
      .replace(/\\biginterleave/g, "\\parallel")
      .replace(/\\boxast/g, "\\square\\mkern-5mu\\ast")
      .replace(/\\boxbar/g, "\\square\\mkern-5mu\\vert")
      .replace(/\\boxbox/g, "\\square\\square")
      .replace(/\\boxbslash/g, "\\square\\mkern-5mu\\backslash")
      .replace(/\\boxcircle/g, "\\square\\mkern-5mu\\circ")
      .replace(/\\boxslash/g, "\\square\\mkern-5mu/")
      .replace(/\\capitalDifferentialD/g, "\\mathrm{D}")
      .replace(/\\differencedelta/g, "\\Delta")
      .replace(/\\differentialD/g, "\\mathrm{d}")
      .replace(/\\divides/g, "\\mid")
      .replace(/\\doubleStruckCapitalN/g, "\\mathbb{N}")
      .replace(/\\doubleStruckCapitalP/g, "\\mathbb{P}")
      .replace(/\\doubleStruckCapitalQ/g, "\\mathbb{Q}")
      .replace(/\\doubleStruckCapitalR/g, "\\mathbb{R}")
      .replace(/\\doubleStruckCapitalZ/g, "\\mathbb{Z}")
      .replace(/\\doubleprime/g, "\\prime\\prime") // or ''
      .replace(/\\enskip/g, "\\;") // or \enspace
      .replace(/\\eqdef/g, "\\triangleq") // or :=
      .replace(/\\error/g, "Error") // explanation placeholder
      .replace(/\\exponentialE/g, "\\mathrm{e}")
      .replace(/\\fatsemi/g, "\\mathbin{;}")
      .replace(/\\fontfamily/g, "\\mathrm{text}")
      .replace(/\\fontseries/g, "\\mathbf{text}")
      .replace(/\\fontshape/g, "\\mathit{text}")
      .replace(/\\gothicCapitalC/g, "\\mathfrak{C}")
      .replace(/\\gothicCapitalH/g, "\\mathfrak{H}")
      .replace(/\\gothicCapitalI/g, "\\mathfrak{I}")
      .replace(/\\gothicCapitalR/g, "\\mathfrak{R}")
      .replace(/\\hspace\*/g, "\\hspace{1em}")
      .replace(/\\imaginaryI/g, "\\mathrm{i}")
      .replace(/\\imaginaryJ/g, "\\mathrm{j}")
      .replace(/\\intclockwise/g, "\\oint")
      .replace(/\\intctrclockwise/g, "\\oint")
      .replace(/\\interleave/g, "\\parallel")
      .replace(/\\laplace/g, "\\mathcal{L}")
      .replace(/\\lb/g, "\\lbrace")
      .replace(/\\leftarrowtriangle/g, "\\triangleleft\\mkern-2mu\\leftarrow")
      .replace(
        /\\leftrightarrowtriangle/g,
        "\\triangleleft\\mkern-2mu\\leftrightarrow\\mkern-2mu\\triangleright",
      )
      .replace(/\\leftslice/g, "/")
      .replace(/\\lightning/g, "\\triangle")
      .replace(/\\longmapsfrom/g, "\\longleftarrow")
      .replace(/\\mVert/g, "\\lVert\\rVert")
      .replace(/\\mapsfrom/g, "\\leftarrow")
      .replace(/\\mathbfit/g, "\\mathbf{\\mathit{x}}")
      .replace(/\\measeq/g, "\\cong")
      .replace(/\\mvert/g, "\\lvert\\rvert")
      .replace(/\\nicefrac/g, "\\frac{a}{b}")
      .replace(/\\nor/g, "\\downarrow")
      .replace(/\\nsubset/g, "\\not\\subset")
      .replace(/\\nsupset/g, "\\not\\supset")
      .replace(/\\ointctrclockwise/g, "\\oint")
      .replace(/\\ordinarycolon/g, "\\colon")
      .replace(/\\originalof/g, "\\cong")
      .replace(/\\overarc/g, "\\overparen{x}")
      .replace(/\\overunderset/g, "\\overset{a}{\\underset{b}{c}}")
      .replace(/\\pdiff/g, "\\partial")
      .replace(/\\questeq/g, "\\overset{?}{=}")
      .replace(/\\rD/g, "\\mathrm{D}")
      .replace(/\\rd/g, "\\mathrm{d}")
      .replace(
        /\\rightarrowtriangle/g,
        "\\rightarrow\\mkern-2mu\\triangleright",
      )
      .replace(/\\rightslice/g, "\\backslash")
      .replace(/\\rmfamily/g, "\\mathrm{text}")
      .replace(/\\roundimplies/g, "\\Rightarrow")
      .replace(/\\scriptCapitalE/g, "\\mathcal{E}")
      .replace(/\\scriptCapitalH/g, "\\mathcal{H}")
      .replace(/\\scriptCapitalL/g, "\\mathcal{L}")
      .replace(/\\scshape/g, "\\text{\\scshape text}")
      .replace(/\\selectfont/g, "")
      .replace(/\\sffamily/g, "\\mathsf{text}")
      .replace(/\\slshape/g, "\\mathit{text}")
      .replace(/\\sslash/g, "\\slash\\slash")
      .replace(/\\stackbin/g, "\\overset{a}{b}")
      .replace(/\\talloblong/g, "\\parallel")
      .replace(/\\ttfamily/g, "\\mathtt{text}")
      .replace(/\\underarc/g, "\\underparen{x}")
      .replace(/\\varointclockwise/g, "\\oint")
      .replace(/\\placeholder/g, "\\Box")
      .replace(/\\~/g, "\\tilde{a}")
      .replace(/\\bigm\{\|\}/g, "\\mid") // if literal
      .replace(/\\bigm\{\|}/g, "\\mid")
      .replace(/\\bigm\|/g, "\\mid") // fallback
      .replace(/\\displaylines/g, "\\;");

    const strippedLatex = normalizedLatex.replace(/^\$\$|\$\$$/g, ""); // remove any wrapping if present

    if (latextextvalue === "" || latextextvalue === null) {
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          const mathNode = new MathNode(strippedLatex);
          selection.insertNodes([mathNode]);
          selection.insertNodes([$createTextNode(" ")]);
          selection.insertText("");
        }
      });
    } else {
      if (strippedLatex === "delete") {
        if (oldMathNode) {
          // Remove the node from the Lexical editor

          editor.update(() => {
            const nodeToDelete = $getNodeByKey(oldMathNode); // Get the node by its key
            if (nodeToDelete) {
              nodeToDelete.remove(); // Remove the node from the editor
            }
          });
        }
        setShowInputTooltip(false);
        setLatextextvalue(null);
        return;
      }

      if (oldMathNode) {
        // Remove the node from the Lexical editor

        editor.update(() => {
          const nodeToDelete = $getNodeByKey(oldMathNode); // Get the node by its key
          if (nodeToDelete) {
            nodeToDelete.remove(); // Remove the node from the editor
          }
        });
      }
      editor.update(() => {
        const mathNode = new MathNode(strippedLatex);
        $insertNodes([mathNode]); // Inserts the MathNode with the LaTeX formula
        // Create and insert a TextNode right after the MathNode
        const textNode = $createTextNode(" "); // Space to allow typing after
        $insertNodes([textNode]);
        setShowInputTooltip(false);
        setLatextextvalue("");
        setInputValue("");
      });
    }
  };

  const insertLink = useCallback(() => {
    if (!isLink) {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, "https://");
    } else {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    }
  }, [editor, isLink]);

  return (
    <div
      className="toolbar"
      ref={toolbarRef}
      style={{ justifyContent: !isLeftPanelOpen && "center" }}
    >
      <button
        disabled={!canUndo}
        onClick={() => {
          editor.dispatchCommand(UNDO_COMMAND);
        }}
        className="toolbar-item spaced"
        aria-label="Undo"
      >
        <i className="format undo" />
      </button>
      <button
        disabled={!canRedo}
        onClick={() => {
          editor.dispatchCommand(REDO_COMMAND);
        }}
        className="toolbar-item"
        aria-label="Redo"
      >
        <i className="format redo" />
      </button>
      <Divider />
      {supportedBlockTypes.has(blockType) && (
        <>
          <button
            className="toolbar-item block-controls"
            onClick={() =>
              setShowBlockOptionsDropDown(!showBlockOptionsDropDown)
            }
            aria-label="Formatting Options"
          >
            <span className={"icon block-type " + blockType} />
            <span className="text">{blockTypeToBlockName[blockType]}</span>
            <i className="chevron-down" />
          </button>
          {showBlockOptionsDropDown &&
            createPortal(
              <BlockOptionsDropdownList
                editor={editor}
                blockType={blockType}
                toolbarRef={toolbarRef}
                setShowBlockOptionsDropDown={setShowBlockOptionsDropDown}
              />,
              document.body,
            )}
          <Divider />
        </>
      )}
      {blockType === "code" ? (
        <>
          <Select
            className="toolbar-item code-language"
            onChange={onCodeLanguageSelect}
            options={codeLanguges}
            value={codeLanguage}
          />
          <i className="chevron-down inside" />
        </>
      ) : (
        <>
          <button
            onClick={() => {
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold");
            }}
            className={"toolbar-item spaced " + (isBold ? "active" : "")}
            aria-label="Format Bold"
          >
            <i className="format bold" />
          </button>
          <button
            onClick={() => {
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, "italic");
            }}
            className={"toolbar-item spaced " + (isItalic ? "active" : "")}
            aria-label="Format Italics"
          >
            <i className="format italic" />
          </button>
          <button
            onClick={() => {
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, "underline");
            }}
            className={"toolbar-item spaced " + (isUnderline ? "active" : "")}
            aria-label="Format Underline"
          >
            <i className="format underline" />
          </button>
          <button
            onClick={() => {
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, "strikethrough");
            }}
            className={
              "toolbar-item spaced " + (isStrikethrough ? "active" : "")
            }
            aria-label="Format Strikethrough"
          >
            <i className="format strikethrough" />
          </button>
          <button
            onClick={() => {
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, "code");
            }}
            className={"toolbar-item spaced " + (isCode ? "active" : "")}
            aria-label="Insert Code"
          >
            <i className="format code" />
          </button>
          <button
            onClick={insertLink}
            className={"toolbar-item spaced " + (isLink ? "active" : "")}
            aria-label="Insert Link"
          >
            <i className="format link" />
          </button>
          {isLink &&
            createPortal(<FloatingLinkEditor editor={editor} />, document.body)}
          <Divider />
          <button
            onClick={() => {
              editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "left");
            }}
            className="toolbar-item spaced"
            aria-label="Left Align"
          >
            <i className="format left-align" />
          </button>
          <button
            onClick={() => {
              editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "center");
            }}
            className="toolbar-item spaced"
            aria-label="Center Align"
          >
            <i className="format center-align" />
          </button>
          <button
            onClick={() => {
              editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "right");
            }}
            className="toolbar-item spaced"
            aria-label="Right Align"
          >
            <i className="format right-align" />
          </button>
          <button
            onClick={() => {
              editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "justify");
            }}
            className="toolbar-item"
            aria-label="Justify Align"
          >
            <i className="format justify-align" />
          </button>{" "}
          {/* Button to toggle input tooltip */}
          {/* <button
            onClick={toggleInputTooltip}
            className="toolbar-item"
            aria-label="Justify Align"
          >
            <i className="format fas fa-superscript" />
          </button> */}
          {/* Conditional rendering of the input tooltip */}
          {/* {showInputTooltip && (
            <div className="input-math-tooltip">
              <InsertMathButton
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                setShowInputTooltip={setShowInputTooltip}
                setLatextextvalue={setLatextextvalue}
                setInputValue={setInputValue}
                oldMathNode={oldMathNode}
                oldnodeeditor={oldnodeeditor}
              />
            </div>
          )} */}
          <IconButton
            style={{ padding: "0px 8px 8px 8px" }}
            onClick={openKeyboard}
            aria-label="open math keyboard"
            data-tooltip-id="math-tooltip2"
          >
            <KeyboardIcon style={{ color: "#5094E7" }} />
            <p style={{ fontSize: "11px", color: "#5094E7" }}>Beta</p>
          </IconButton>
          <Tooltip
            id="math-tooltip2"
            place="top"
            content="Beta version: Results may not be as expected."
            effect="solid"
            type="light"
          />
        </>
      )}

      <div className="workboard-enhance-formatting">
        {!disableEnhanceText && (
          <>
            {reFormatterLoading ? (
              <div style={{ width: "166px", margin: "-12px auto 0px auto" }}>
                <div className="loading-spinner" />
              </div>
            ) : (
              <button
                data-tooltip-id="enhance-button-tooltip"
                onClick={handleEnhanceText}
                style={{
                  cursor:
                    ganrateLesson || reFormatterLoading
                      ? "not-allowed"
                      : "pointer",
                }}
                disabled={ganrateLesson || reFormatterLoading}
              >
                <img src={magicpen} />
                Enhance Text
              </button>
            )}
          </>
        )}

        <Tooltip
          id="enhance-button-tooltip"
          place="top"
          content={
            ganrateLesson
              ? "Please wait"
              : reFormatterLoading
                ? "Enhancement is going on, please wait"
                : "Click to enhance the text"
          }
          type="light"
          effect="float"
        />
      </div>
      <MathKeyboardModal
        isOpen={isModalOpen || showInputTooltip}
        onClose={closeKeyboard}
        onInsert={handleInsert}
        latextextvalue={latextextvalue}
      />

      <Tooltip id="math-tooltip" place="top" effect="solid" type="light" />
    </div>
  );
}
